import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Handle, Position, useReactFlow, useConnection, useEdges } from '@xyflow/react';
import get from 'lodash/get';
import filter from 'lodash/filter';
import { useSourceHandle, useTargetHandle, getClosestVerticalTargetHandlePosition, getTargetHandleKey } from '../../util/handleUtils';
import WorkflowContext from 'src/views/SystemSettings/Workflow/WorkflowContext';

function WorkflowStepHandles({ id, positionAbsoluteX: x, positionAbsoluteY: y, width, height, isConnectable }) {
  // The state of the UWE workflow
  const { state } = useContext(WorkflowContext);

  // Susbcribe to any edge changes
  const edges = useEdges();

  // Subscribe to any active connection events
  const connection = useConnection();

  // Standard handles
  const sourceHandle = useSourceHandle(Position.Right, id);
  const leftTargetHandle = useTargetHandle(Position.Left, id);
  const topTargetHandle = useTargetHandle(Position.Top, id);
  const bottomTargetHandle = useTargetHandle(Position.Bottom, id);

  // Get info from UWE step state
  const step = useMemo(() => get(state, ['steps', id]) || {}, [state, id]);
  const isInitialStep = useMemo(() => !!step.initial, [step]);
  const isEndStep = useMemo(() => step.type === 'end', [step]);

  /**
   * Maintain state of active handles based on received edges or on listened connections
   *
   * Initialize it with whatever comes from edges, then add to it
   *
   * Then, update incoming edges to point to the best suited handle relative to
   * this node's position, whenever this node is moved.
   */
  const activeTargets = useMemo(() => filter(edges, e => e.target === id).map(e => e.targetHandle), [edges]);

  /**
   * During an active connection, if the connection gets close
   * to the top or bottom of a node, activate the source handles there
   */
  const [temporaryTarget, setTemporaryTarget] = useState(null);

  useEffect(() => {
    if (!connection.to || !connection.from || !connection.inProgress) {
      setTemporaryTarget(null);
      return;
    }

    // If stuck to a handle, mark it as the temporary target
    if (connection.toHandle) {
      setTemporaryTarget(connection.toHandle.id);
      return;
    }

    const tempPos = getClosestVerticalTargetHandlePosition(connection.to, { height, y });
    if (!tempPos) {
      setTemporaryTarget(null);
      return;
    }

    const tempHandle = getTargetHandleKey(tempPos, id);
    if (tempHandle !== temporaryTarget) {
      setTemporaryTarget(tempHandle);
    }
  }, [connection]);

  /**
   * Get the handles
   */

  const visibleTargetHandles = useMemo(() => {
    const visible = new Set();

    if (bottomTargetHandle === temporaryTarget || activeTargets.includes(bottomTargetHandle)) {
      visible.add(bottomTargetHandle);
    }

    if (topTargetHandle === temporaryTarget || activeTargets.includes(topTargetHandle)) {
      visible.add(topTargetHandle);
    }

    return visible;
  }, [temporaryTarget, activeTargets]);

  /* If handles are conditionally rendered and not present initially, you need to update the node internals https://reactflow.dev/docs/api/hooks/use-update-node-internals/ */
  /* In this case we don't need to use useUpdateNodeInternals, since !isConnecting is true at the beginning and all handles are rendered initially. */
  return (
    <>
      <>
        <Handle
          style={{ opacity: Number(!isEndStep) }}
          id={sourceHandle}
          position={Position.Right}
          isConnectable={isConnectable}
          isConnectableStart={true}
          isConnectableEnd={false}
          type="source"
        />
      </>
      <>
        <Handle
          style={{ opacity: Number(!isInitialStep) }}
          id={leftTargetHandle}
          position={Position.Left}
          isConnectable={isConnectable}
          isConnectableStart={false}
          isConnectableEnd={true}
          type="target"
        />
        <Handle
          style={{ opacity: Number(!isInitialStep && visibleTargetHandles.has(topTargetHandle)) }}
          id={topTargetHandle}
          position={Position.Top}
          isConnectable={isConnectable}
          isConnectableStart={false}
          isConnectableEnd={true}
          type="target"
        />
        <Handle
          style={{ opacity: Number(!isInitialStep && visibleTargetHandles.has(bottomTargetHandle)) }}
          id={bottomTargetHandle}
          position={Position.Bottom}
          isConnectable={isConnectable}
          isConnectableStart={false}
          isConnectableEnd={true}
          type="target"
        />
      </>
    </>
  );
}

export default WorkflowStepHandles;
